import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, X, ArrowRight, Star, Shield, Users, Zap, Clock, Target, FileX, Search, AlertTriangle, TrendingDown, Sparkles, BarChart3, Eye, Gauge, Rocket, Award, Brain, Layers, Globe, Briefcase, Trophy, ChevronDown, Play, Pause, Volume2, Settings, Database, UserCheck, Timer, LineChart, Activity, MousePointer, CheckCircle2, XCircle, TrendingUp, Monitor, Smartphone, Tablet, MessageCircle, Quote, Building2, GraduationCap, Lock, FileText, Clipboard, PenTool, Cog, BarChart, Users2, MessageSquare, FileBarChart, History, VideoIcon, ScreenShare, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DemoRequestForm } from './DemoRequestForm';
import { DemoRequestDialog } from './DemoRequestDialog';
import { FooterSection } from './FooterSection';
import { NewPricingSection } from "./NewPricingSection";
import { FeatureSpotlightGrid } from './FeatureSpotlightGrid';
import { CompetitiveComparisonTable } from './CompetitiveComparisonTable';
import { CaseStudiesShowcase } from './CaseStudiesShowcase';

// Country codes list with country names
const countryCodes = [{
  code: "+91",
  country: "India"
}, {
  code: "+1",
  country: "United States"
}, {
  code: "+44",
  country: "United Kingdom"
}, {
  code: "+61",
  country: "Australia"
}, {
  code: "+86",
  country: "China"
}, {
  code: "+49",
  country: "Germany"
}, {
  code: "+33",
  country: "France"
}, {
  code: "+81",
  country: "Japan"
}, {
  code: "+7",
  country: "Russia"
}, {
  code: "+55",
  country: "Brazil"
}, {
  code: "+27",
  country: "South Africa"
}, {
  code: "+971",
  country: "UAE"
}, {
  code: "+966",
  country: "Saudi Arabia"
}, {
  code: "+65",
  country: "Singapore"
}, {
  code: "+82",
  country: "South Korea"
}, {
  code: "+39",
  country: "Italy"
}, {
  code: "+34",
  country: "Spain"
}, {
  code: "+52",
  country: "Mexico"
}, {
  code: "+60",
  country: "Malaysia"
}, {
  code: "+64",
  country: "New Zealand"
}, {
  code: "+31",
  country: "Netherlands"
}, {
  code: "+48",
  country: "Poland"
}, {
  code: "+46",
  country: "Sweden"
}, {
  code: "+41",
  country: "Switzerland"
}];

// Process Slider Component
function ProcessSlider() {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const steps = [{
    id: 0,
    title: "Enter Job Description",
    icon: FileText,
    description: "All you need to do is enter or paste your job description. 80% of your work done here.",
    color: "from-green-500 to-green-600",
    bgColor: "from-green-500/20 to-green-500/10",
    accentColor: "text-green-500",
    badgeColor: "bg-green-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Enter%20Job%20Description.gif",
    duration: 9000 // 9 seconds
  }, {
    id: 1,
    title: "Select Question Distribution",
    icon: Settings,
    description: "Select difficulty for each skills type and choose from MCQs, Essay, Audio, and Video answers to evaluate your applicants on a multi-modal platform",
    color: "from-blue-500 to-blue-600",
    bgColor: "from-blue-500/20 to-blue-500/10",
    accentColor: "text-blue-500",
    badgeColor: "bg-blue-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Select%20Question%20Distribution.gif",
    duration: 10800 // 10.8 seconds
  }, {
    id: 2,
    title: "Get AI Questions",
    icon: Brain,
    description: "Testera generates a detailed skills-based assessment for you. Each question is assigned a skill, marks, timer, difficulty, and a marking criteria.",
    color: "from-purple-500 to-purple-600",
    bgColor: "from-purple-500/20 to-purple-500/10",
    accentColor: "text-purple-500",
    badgeColor: "bg-purple-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Get%20AI%20Questions.gif",
    duration: 12000 // 12 seconds
  }, {
    id: 3,
    title: "Customize Questions",
    icon: PenTool,
    description: "Make each question truly yours by getting options to edit every single parameter - skills, marks, timer, difficulty, mark scheme, MCQ options, or even the full question itself.",
    color: "from-orange-500 to-orange-600",
    bgColor: "from-orange-500/20 to-orange-500/10",
    accentColor: "text-orange-500",
    badgeColor: "bg-orange-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Customise%20Questions.gif",
    duration: 11000 // 11 seconds
  }, {
    id: 4,
    title: "Share Test",
    icon: Users2,
    description: "Publish the test and you're all set to receive applications. Share the test with all aspiring applicants and wait for results!",
    color: "from-teal-500 to-teal-600",
    bgColor: "from-teal-500/20 to-teal-500/10",
    accentColor: "text-teal-500",
    badgeColor: "bg-teal-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Share%20Test.gif",
    duration: 8300 // 8.3 seconds
  }, {
    id: 5,
    title: "Receive applications",
    icon: UserCheck,
    description: "Let the candidates give the test in their own time. Testera's advanced proctoring tools monitor the screen for any cheating and unethical behaviour so you don't have to worry about dishonest applicants.",
    color: "from-cyan-500 to-cyan-600",
    bgColor: "from-cyan-500/20 to-cyan-500/10",
    accentColor: "text-cyan-500",
    badgeColor: "bg-cyan-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Receive%20Applications.gif",
    duration: 13000 // 13 seconds
  }, {
    id: 6,
    title: "Review Results",
    icon: BarChart,
    description: "Testera evaluates all submissions for you and gives detailed analytics and feedback for each question. It's time to interview the top scorers!",
    color: "from-indigo-500 to-indigo-600",
    bgColor: "from-indigo-500/20 to-indigo-500/10",
    accentColor: "text-indigo-500",
    badgeColor: "bg-indigo-500",
    gifUrl: "https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/Shortlist%20with%20AI%20Scoring.gif",
    duration: 17500 // 17.5 seconds
  }];

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    const gifDuration = steps[activeStep].duration; // Use specific duration for current GIF
    const progressInterval = 50; // Update progress every 50ms
    const totalSteps = gifDuration / progressInterval;
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 100 / totalSteps;
        if (newProgress >= 100) {
          // Move to next step
          setActiveStep(current => (current + 1) % steps.length);
          return 0; // Reset progress
        }
        return newProgress;
      });
    }, progressInterval);
    return () => clearInterval(interval);
  }, [activeStep, isAutoPlaying, steps.length]);

  // Reset progress when manually changing steps
  const handleStepClick = (stepId: number) => {
    setActiveStep(stepId);
    setProgress(0);
    setIsAutoPlaying(false); // Pause auto-play when user manually selects

    // Resume auto-play after 3 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };
  return <div className="space-y-8">
      {/* Main Display Area - Joined Cards */}
      <div className="bg-card rounded-xl shadow-2xl">
        {/* GIF Display Area */}
        <div className="relative bg-gradient-to-br from-muted/30 to-muted/10 p-8 flex items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5"></div>
          
          {/* GIF Display */}
          <div className="relative z-10 w-full">
            <div className="rounded-lg shadow-lg">
              <img src={steps[activeStep].gifUrl} alt={steps[activeStep].title} className="w-full h-auto rounded-lg" />
            </div>
          </div>

          {/* Progress indicator */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <div className="w-32 h-1 bg-muted/50 rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${steps[activeStep].color} transition-all duration-100 ease-linear`} style={{
              width: `${progress}%`
            }} />
            </div>
          </div>
        </div>

        {/* Interactive Step Buttons - Horizontal Scroll for 7 items */}
        <div className="bg-card border-t border-border">
          <div className="flex overflow-x-auto gap-0 scrollbar-hide scroll-smooth" id="steps-container">
            {steps.map((step, index) => <button key={step.id} onClick={() => handleStepClick(step.id)} className={`
                  relative p-4 text-left transition-all duration-300 group min-w-[240px] max-w-[240px] flex-shrink-0
                  ${activeStep === step.id ? 'bg-gradient-to-br from-primary/10 to-secondary/5 border-t-2 border-primary' : 'hover:bg-muted/50 border-t-2 border-transparent hover:border-muted-foreground/20'}
                  ${index !== steps.length - 1 ? 'border-r border-border' : ''}
                `} ref={activeStep === step.id ? el => {
            if (el && !el.dataset.scrolled) {
              el.dataset.scrolled = 'true';
              const container = document.getElementById('steps-container');
              if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = el.getBoundingClientRect();
                const scrollLeft = container.scrollLeft + elementRect.left - containerRect.left - containerRect.width / 2 + elementRect.width / 2;
                container.scrollTo({
                  left: scrollLeft,
                  behavior: 'smooth'
                });
              }
            }
          } : undefined}>
                {/* Active step indicator */}
                {activeStep === step.id && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary animate-pulse" />}

                <div className="flex items-start space-x-3 h-full">
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center relative flex-shrink-0 mt-1
                    transition-all duration-300 group-hover:scale-110 shadow-lg
                    ${activeStep === step.id ? `bg-gradient-to-br ${step.color}` : `bg-gradient-to-br ${step.bgColor}`}
                  `}>
                    {React.createElement(step.icon, {
                  className: `h-4 w-4 ${activeStep === step.id ? 'text-white' : step.accentColor} transition-colors duration-300`
                })}
                    <div className={`absolute -top-1 -right-1 w-4 h-4 ${step.badgeColor} rounded-full flex items-center justify-center text-white text-[10px] font-bold`}>
                      {index + 1}
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-2 min-w-0 py-1">
                    <h3 className={`text-sm font-semibold transition-colors duration-300 leading-tight ${activeStep === step.id ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300 overflow-hidden">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Hover effect indicator */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
              </button>)}
          </div>
        </div>
      </div>
    </div>;
}

// Features Showcase Component
function FeaturesShowcase() {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const features = [{
    name: "Reduce Time to Hire by 75%",
    icon: Clock,
    description: "Instantly screen hundreds of candidates. Focus your time on only the most qualified applicants.",
    category: "Efficiency",
    color: "from-blue-500 to-cyan-500"
  }, {
    name: "Improve Quality of Hire",
    icon: Trophy,
    description: "Job-specific assessments and skills-based evaluation identify top performers before interviews.",
    category: "Quality",
    color: "from-green-500 to-emerald-500"
  }, {
    name: "Scale Your Hiring Process",
    icon: TrendingUp,
    description: "Handle 10x more applications without increasing your recruitment team size.",
    category: "Growth",
    color: "from-purple-500 to-violet-500"
  }, {
    name: "Cut Recruitment Costs",
    icon: Target,
    description: "Reduce cost-per-hire by 60%. No more expensive job boards or recruitment agencies.",
    category: "Cost Savings",
    color: "from-orange-500 to-red-500"
  }, {
    name: "Make Data-Driven Decisions",
    icon: BarChart3,
    description: "Get objective candidate scores and analytics to eliminate hiring bias and improve outcomes.",
    category: "Intelligence",
    color: "from-teal-500 to-cyan-500"
  }, {
    name: "Enhance Candidate Experience",
    icon: Users,
    description: "Modern, mobile-friendly assessments that candidates actually enjoy taking.",
    category: "Experience",
    color: "from-indigo-500 to-blue-500"
  }];
  return <div className="max-w-5xl mx-auto">
      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => <Card key={index} className="relative group cursor-pointer border-2 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden" onMouseEnter={() => setHoveredFeature(index)} onMouseLeave={() => setHoveredFeature(null)}>
            {/* Animated background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-all duration-500`} />
            
            {/* Floating animation dots */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-700">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} />
                <div className="w-2 h-2 bg-secondary/60 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} />
                <div className="w-2 h-2 bg-accent/60 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} />
              </div>
            </div>

            <CardContent className="p-6 space-y-4 relative z-10">
              <div className="flex items-start space-x-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 bg-gradient-to-br ${feature.color} shadow-lg`}>
                  <feature.icon className="h-7 w-7 text-white drop-shadow-sm" />
                </div>
                
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                      {feature.category}
                    </Badge>
                  </div>
                  
                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors duration-300 text-xl">
                    {feature.name}
                  </h3>
                  
                  <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </div>

              {/* Progress indicator */}
              <div className="relative">
                <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${feature.color} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out`} />
                </div>
              </div>

              {/* Hover effect indicator */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
            </CardContent>
          </Card>)}
      </div>
    </div>;
}
export function AdsLandingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Animation trigger on component mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Rotating steps for demo
  const demoSteps = [{
    icon: Zap,
    text: "AI generates questions",
    color: "text-yellow-500"
  }, {
    icon: Users,
    text: "Candidates take assessment",
    color: "text-blue-500"
  }, {
    icon: BarChart3,
    text: "Instant scoring & ranking",
    color: "text-green-500"
  }];
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % demoSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const scrollToForm = () => {
    document.getElementById('early-access-form')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen relative overflow-hidden">
      {/* Floating CTA removed - single demo focus */}

      {/* Section 1: Hero With Form */}
      <section 
        className="relative min-h-screen py-32 lg:py-40 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://sekiwtiutnikmdbpiufi.supabase.co/storage/v1/object/public/siteassets/HeroSection.jpg')`
        }}
      >
        {/* Dark overlay for text visibility */}
        <div className="absolute inset-0 bg-black/65"></div>
        {/* Enhanced animated background elements with stronger visibility */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/12 rounded-full blur-3xl animate-pulse opacity-80"></div>
          <div className="absolute bottom-20 right-1/4 w-112 h-112 bg-secondary/10 rounded-full blur-3xl animate-bounce opacity-75"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-accent/8 rounded-full blur-2xl animate-ping opacity-70"></div>
          
          {/* Enhanced floating icons with stronger animations */}
          <div className="absolute top-32 right-20 opacity-25 animate-float" style={{
          animationDuration: '4s'
        }}>
            <Rocket className="h-16 w-16 text-primary" />
          </div>
          <div className="absolute bottom-32 left-20 opacity-25 animate-float-delayed" style={{
          animationDuration: '5s'
        }}>
            <Trophy className="h-20 w-20 text-secondary" />
          </div>
          <div className="absolute top-1/3 right-1/3 opacity-20 animate-spin" style={{
          animationDuration: '12s'
        }}>
            <Settings className="h-14 w-14 text-accent" />
          </div>
        </div>

        <div className="container mx-auto px-6 lg:px-24 relative pt-20">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow-lg">
                  Too Many Resumes. Too Little Time?{" "}
                  <span className="text-orange-400 relative drop-shadow-lg">
                    Let AI Handle Your First Round.
                  </span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed drop-shadow-md">
                  Create job-specific assessments in seconds. Automatically evaluate candidates. Hire faster with confidence.
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                  <Button 
                    onClick={() => setDemoDialogOpen(true)}
                    size="lg"
                    className="bg-[#e16702] hover:bg-[#d45c02] text-white px-8 py-5 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 h-16"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    Book a Demo
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    variant="outline"
                    size="lg"
                    className="border-2 border-white bg-white/10 text-white hover:bg-white hover:text-black px-8 py-5 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 h-16"
                  >
                    Sign Up
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </div>
              </div>
              
              {/* Trust indicators */}
              <div className="flex items-center justify-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-white/80">No Credit Card</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-white/80">Privacy Protected</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Timer className="h-5 w-5 text-purple-500" />
                  <span className="text-sm text-white/80">2 Min Setup</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Product in Action */}
      <section className="py-24 bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">

        <div className="container mx-auto px-6 lg:px-24 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Assess. Score. Shortlist.{" "}
              <span className="text-[#e16702] font-bold">
                All in One Flow.
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Testera generates assessments, monitors responses, and ranks candidates — all with AI precision.
            </p>
            
            {/* Interactive feature showcase */}
            <div className="flex justify-center items-center space-x-8 mt-8">
              <div className="flex items-center space-x-2 text-green-500">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">30-sec Setup</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-500">
                <Zap className="h-5 w-5" />
                <span className="text-sm font-medium">Instant Scoring</span>
              </div>
              <div className="flex items-center space-x-2 text-purple-500">
                <Brain className="h-5 w-5" />
                <span className="text-sm font-medium">AI-Powered</span>
              </div>
            </div>
          </div>
          
          <ProcessSlider />
        </div>
      </section>

      {/* New Sections: Feature Spotlight, Competitive Comparison, Case Studies */}
      <FeatureSpotlightGrid />
      <CompetitiveComparisonTable />
      <CaseStudiesShowcase />

      {/* Section 8: Pricing */}
      <section className="py-24 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">

        <div className="relative">
          <NewPricingSection />
        </div>
      </section>

      {/* Section 8: Repeated CTA */}
      <section className="py-24 bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">

        <div className="container mx-auto px-6 lg:px-24 text-center relative z-10">
          <div className="max-w-5xl mx-auto space-y-12">
            <h2 className="text-4xl lg:text-6xl font-bold text-foreground leading-tight">
              Start Hiring{" "}
              <span className="text-[#e16702] font-bold">
                Smarter
              </span>{" "}
              Today
            </h2>
            
            <p className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              No more guesswork. No more resume fatigue. Let AI shortlist the right candidates for you.
            </p>
            
            {/* Enhanced CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button size="lg" onClick={scrollToForm} className="relative text-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg group overflow-hidden bg-[#e16702] hover:bg-[#c5550a]">
                {/* Button background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                <Rocket className="mr-2 h-5 w-5 group-hover:animate-bounce" />
                Get Free Demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
              
              
            </div>
            
            {/* Trust signals */}
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>No credit card needed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <span>Setup in 5 minutes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-purple-500" />
                  <span>Enterprise-grade security</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <FooterSection />
      
      {/* Demo Request Dialog */}
      <DemoRequestDialog 
        open={demoDialogOpen} 
        onOpenChange={setDemoDialogOpen} 
      />
    </div>;
}

// Add custom animations to index.css if not already present
const styles = `
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

@keyframes float-delayed {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-30px); }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes fade-in-delayed {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 4s ease-in-out infinite;
}

.animate-spin-slow {
  animation: spin-slow 8s linear infinite;
}

.animate-fade-in-delayed {
  animation: fade-in-delayed 0.8s ease-out 0.3s both;
}
`;